import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { productsAPI, uploadAPI } from '../services/api';
import { ArrowLeft, Upload, X, Package, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { PRODUCT_SIZES, PRODUCT_COLORS } from '../utils/constants';

const CreateProduct = () => {
  const navigate = useNavigate();
  const { collectionId, productId } = useParams();
  const isEditMode = Boolean(productId);
  const { addProduct, updateProduct, collections, products } = useApp();
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    collectionId: collectionId || '',
    sizes: [],
    colors: [],
    sizeStock: {},
    description: '',
    images: [],
    rating: '',
    reviewCount: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const replaceIndexRef = useRef(null);
  const replaceInputRef = useRef(null);

  useEffect(() => {
    if (!isEditMode) return;

    const loadProduct = async () => {
      try {
        setIsLoading(true);
        const existing = products.find((p) => p.id === productId);

        const product = existing || (await productsAPI.getById(productId)).product;

        if (!product) {
          setErrors({ general: 'Product not found' });
          return;
        }

        const collectionRef = product.collectionId || product.collection_id || '';
        const sizeStock = product.sizeStock || product.size_stock || {};
        const images = product.images || [];

        setFormData({
          name: product.name || '',
          price: product.price != null ? String(product.price) : '',
          collectionId: collectionRef,
          sizes: product.sizes || [],
          colors: product.colors || [],
          sizeStock,
          description: product.description || '',
          images,
          rating: product.rating != null ? String(product.rating) : '',
          reviewCount: (product.reviewCount ?? product.review_count) != null
            ? String(product.reviewCount ?? product.review_count)
            : '',
        });
        setImagePreviews(images);
      } catch (error) {
        console.error('Error loading product:', error);
        setErrors({ general: error.message || 'Failed to load product' });
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [productId, isEditMode, products]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSizeToggle = (size) => {
    setFormData(prev => {
      const newSizes = prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size];
      
      // Update size stock - add new size with 0 stock, or remove size from stock
      const newSizeStock = { ...prev.sizeStock };
      if (newSizes.includes(size) && !prev.sizes.includes(size)) {
        newSizeStock[size] = 0;
      } else if (!newSizes.includes(size) && prev.sizes.includes(size)) {
        delete newSizeStock[size];
      }
      
      return {
        ...prev,
        sizes: newSizes,
        sizeStock: newSizeStock
      };
    });
  };

  const handleSizeStockChange = (size, stock) => {
    const parsed = stock === '' ? 0 : parseInt(stock, 10);
    setFormData(prev => ({
      ...prev,
      sizeStock: {
        ...prev.sizeStock,
        [size]: Number.isFinite(parsed) && parsed >= 0 ? parsed : 0
      }
    }));
  };

  const markAllSizesOutOfStock = () => {
    if (formData.sizes.length === 0) return;
    setFormData(prev => {
      const newSizeStock = { ...prev.sizeStock };
      prev.sizes.forEach((size) => {
        newSizeStock[size] = 0;
      });
      return { ...prev, sizeStock: newSizeStock };
    });
    if (errors.sizeStock) {
      setErrors(prev => ({ ...prev, sizeStock: '' }));
    }
  };

  const handleColorToggle = (color) => {
    setFormData(prev => ({
      ...prev,
      colors: prev.colors.includes(color)
        ? prev.colors.filter(c => c !== color)
        : [...prev.colors, color]
    }));
  };

  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // Validate files
    const validFiles = [];
    
    for (const file of files) {
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({ ...prev, images: 'Please select only valid image files' }));
        continue;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, images: 'Each image should be less than 5MB' }));
        continue;
      }
      
      validFiles.push(file);
    }
    
    if (validFiles.length === 0) return;
    
    setIsSubmitting(true);
    
    try {
      // Upload multiple images to Cloudinary
      const response = await uploadAPI.uploadImages(validFiles);
      const uploadedImages = response.images;
      
      uploadedImages.forEach((imageInfo) => {
        setImagePreviews(prev => [...prev, imageInfo.url]);
        setFormData(prev => ({ 
          ...prev, 
          images: [...prev.images, imageInfo.url]
        }));
      });
      
      setErrors(prev => ({ ...prev, images: '' }));
      
    } catch (error) {
      console.error('Image upload error:', error);
      setErrors(prev => ({ ...prev, images: 'Failed to upload images. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUrlAdd = async (url) => {
    if (!url.trim()) return;
    
    try {
      // Upload URL image to Cloudinary
      const response = await uploadAPI.uploadImageBase64(url);
      const cloudinaryUrl = response.image.url;
      
      setImagePreviews(prev => [...prev, cloudinaryUrl]);
      setFormData(prev => ({ 
        ...prev, 
        images: [...prev.images, cloudinaryUrl]
      }));
    } catch (error) {
      console.error('URL image upload error:', error);
      // If Cloudinary upload fails, use URL directly
      setImagePreviews(prev => [...prev, url]);
      setFormData(prev => ({ 
        ...prev, 
        images: [...prev.images, url]
      }));
    }
  };

  const removeImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({ 
      ...prev, 
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const reorderList = (list, from, to) => {
    const updated = [...list];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    return updated;
  };

  const moveImage = (from, to) => {
    if (to < 0 || to >= imagePreviews.length || from === to) return;
    setImagePreviews(prev => reorderList(prev, from, to));
    setFormData(prev => ({
      ...prev,
      images: reorderList(prev.images, from, to)
    }));
  };

  const handleDragStart = (index) => setDraggedIndex(index);

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (dragOverIndex !== index) setDragOverIndex(index);
  };

  const handleDrop = (event, index) => {
    event.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveImage(draggedIndex, index);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const startReplaceImage = (index) => {
    replaceIndexRef.current = index;
    replaceInputRef.current?.click();
  };

  const handleReplaceImageChange = async (e) => {
    const file = e.target.files?.[0];
    const index = replaceIndexRef.current;
    e.target.value = '';
    if (!file || index === null) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, images: 'Please select a valid image file' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, images: 'Each image should be less than 5MB' }));
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await uploadAPI.uploadImages([file]);
      const newUrl = response.images?.[0]?.url;
      if (!newUrl) throw new Error('Upload failed');

      const replaceAt = (list) => list.map((item, i) => (i === index ? newUrl : item));
      setImagePreviews(prev => replaceAt(prev));
      setFormData(prev => ({ ...prev, images: replaceAt(prev.images) }));
      setErrors(prev => ({ ...prev, images: '' }));
    } catch (error) {
      console.error('Image replace error:', error);
      setErrors(prev => ({ ...prev, images: 'Failed to replace image. Please try again.' }));
    } finally {
      setIsSubmitting(false);
      replaceIndexRef.current = null;
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    if (!formData.price || isNaN(formData.price) || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Please enter a valid price';
    }
    
    if (!formData.collectionId) {
      newErrors.collectionId = 'Please select a collection';
    }
    
    if (formData.sizes.length === 0) {
      newErrors.sizes = 'Please select at least one size';
    }
    
    if (formData.colors.length === 0) {
      newErrors.colors = 'Please select at least one color';
    }
    
    // Size stock can be 0 for every size (product still lists as Out of Stock)
    if (formData.sizes.length > 0) {
      const missingStock = formData.sizes.some(
        (size) => formData.sizeStock[size] === undefined || formData.sizeStock[size] === null || formData.sizeStock[size] === ''
      );
      if (missingStock) {
        newErrors.sizeStock = 'Please enter stock for each selected size (0 is allowed for out of stock)';
      } else {
        const invalidStock = formData.sizes.some((size) => {
          const stock = Number(formData.sizeStock[size]);
          return !Number.isFinite(stock) || stock < 0;
        });
        if (invalidStock) {
          newErrors.sizeStock = 'Stock quantities must be 0 or greater';
        }
      }
    }
    
    if (formData.images.length === 0) {
      newErrors.images = 'At least one product image is required';
    }
    
    if (formData.rating !== '' && (isNaN(formData.rating) || parseFloat(formData.rating) < 0 || parseFloat(formData.rating) > 5)) {
      newErrors.rating = 'Rating must be between 0 and 5';
    }

    if (formData.reviewCount !== '' && (isNaN(formData.reviewCount) || parseInt(formData.reviewCount, 10) < 0)) {
      newErrors.reviewCount = 'Review count must be 0 or greater';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const productData = {
        name: formData.name.trim(),
        price: parseFloat(formData.price),
        collection_id: formData.collectionId,
        sizes: formData.sizes,
        colors: formData.colors,
        size_stock: formData.sizeStock,
        description: formData.description.trim(),
        images: formData.images,
        rating: formData.rating !== '' ? parseFloat(formData.rating) : null,
        review_count: formData.reviewCount !== '' ? parseInt(formData.reviewCount, 10) : null,
      };
      
      if (isEditMode) {
        await updateProduct(productId, productData);
      } else {
        await addProduct(productData);
      }

      const returnCollectionId = formData.collectionId || collectionId;
      if (returnCollectionId) {
        navigate(`/products/${returnCollectionId}`);
      } else {
        navigate('/products');
      }
    } catch (error) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} product:`, error);
      setErrors({
        general: error.message || `Failed to ${isEditMode ? 'update' : 'create'} product. Please try again.`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    const returnCollectionId = formData.collectionId || collectionId;
    if (returnCollectionId) {
      navigate(`/products/${returnCollectionId}`);
    } else {
      navigate('/products');
    }
  };

  const selectedCollection = collections.find(
    (c) => String(c.id) === String(formData.collectionId)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <button 
          onClick={handleCancel}
          className="inline-flex items-center text-indigo-600 hover:text-indigo-700 mb-4"
        >
          <ArrowLeft size={16} className="mr-1" />
          Back to Products
        </button>
        <h1 className="text-2xl font-bold text-gray-900">
          {isEditMode ? 'Edit Product' : 'Add New Product'}
        </h1>
        <p className="text-gray-600 mt-1">
          {isEditMode
            ? 'Update product details and inventory'
            : selectedCollection
              ? `Add a product to ${selectedCollection.name}`
              : 'Add a new product to your inventory'}
        </p>
      </div>

      {errors.general && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="e.g., Cotton T-Shirt"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.name ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.name && (
                    <p className="text-red-600 text-sm mt-1">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="collectionId" className="block text-sm font-medium text-gray-700 mb-2">
                    Collection *
                  </label>
                  <select
                    id="collectionId"
                    name="collectionId"
                    value={formData.collectionId}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.collectionId ? 'border-red-300' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select a collection</option>
                    {collections.map((collection) => (
                      <option key={collection.id} value={collection.id}>
                        {collection.name}
                      </option>
                    ))}
                  </select>
                  {errors.collectionId && (
                    <p className="text-red-600 text-sm mt-1">{errors.collectionId}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-2">
                    Price (₹) *
                  </label>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="999"
                    step="0.01"
                    min="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.price ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.price && (
                    <p className="text-red-600 text-sm mt-1">{errors.price}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-2">
                    Star Rating (0–5)
                  </label>
                  <input
                    type="number"
                    id="rating"
                    name="rating"
                    value={formData.rating}
                    onChange={handleInputChange}
                    placeholder="4.5"
                    step="0.1"
                    min="0"
                    max="5"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.rating ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.rating && (
                    <p className="text-red-600 text-sm mt-1">{errors.rating}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="reviewCount" className="block text-sm font-medium text-gray-700 mb-2">
                    Review Count
                  </label>
                  <input
                    type="number"
                    id="reviewCount"
                    name="reviewCount"
                    value={formData.reviewCount}
                    onChange={handleInputChange}
                    placeholder="124"
                    min="0"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                      errors.reviewCount ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {errors.reviewCount && (
                    <p className="text-red-600 text-sm mt-1">{errors.reviewCount}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Leave both rating and review count empty to hide stars on the product page.
                  </p>
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows={3}
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your product..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>

            {/* Sizes */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Sizes *</h3>
              <div className="flex flex-wrap gap-2">
                {PRODUCT_SIZES.map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleSizeToggle(size)}
                    className={`px-4 py-2 rounded-lg border transition-colors ${
                      formData.sizes.includes(size)
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {size}
                  </button>
                ))}
              </div>
              {errors.sizes && (
                <p className="text-red-600 text-sm mt-2">{errors.sizes}</p>
              )}
              
              {/* Size-wise Stock */}
              {formData.sizes.length > 0 && (
                <div className="mt-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <h4 className="text-md font-medium text-gray-900">Stock Quantity by Size *</h4>
                    <button
                      type="button"
                      onClick={markAllSizesOutOfStock}
                      className="text-sm px-3 py-1.5 rounded-lg border border-red-200 text-red-700 bg-red-50 hover:bg-red-100 transition-colors"
                    >
                      Mark all out of stock
                    </button>
                  </div>
                  <p className="text-sm text-gray-500 mb-3">
                    Set stock to 0 for any size to show it as out of stock on the website. The product will still appear in the store.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {formData.sizes.map((size) => (
                      <div key={size}>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          {size} Size
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={formData.sizeStock[size] ?? 0}
                          onChange={(e) => handleSizeStockChange(size, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="0"
                        />
                      </div>
                    ))}
                  </div>
                  {errors.sizeStock && (
                    <p className="text-red-600 text-sm mt-2">{errors.sizeStock}</p>
                  )}
                  <div className="mt-2 text-sm text-gray-600">
                    Total Stock: {Object.values(formData.sizeStock).reduce((sum, stock) => sum + (parseInt(stock) || 0), 0)} units
                    {Object.values(formData.sizeStock).reduce((sum, stock) => sum + (parseInt(stock) || 0), 0) === 0 && formData.sizes.length > 0 && (
                      <span className="ml-2 text-red-600 font-medium">(All sizes out of stock)</span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Colors */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Colors *</h3>
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {PRODUCT_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => handleColorToggle(color)}
                    className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                      formData.colors.includes(color)
                        ? 'bg-indigo-100 border-indigo-300 text-indigo-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {color}
                  </button>
                ))}
              </div>
              {errors.colors && (
                <p className="text-red-600 text-sm mt-2">{errors.colors}</p>
              )}
            </div>
          </div>

          {/* Image Upload */}
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Image *</h3>
              
                          {/* File Upload */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Images (Multiple)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-300 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">
                    Click to upload multiple images
                  </span>
                  <span className="text-xs text-gray-500 mt-1">
                    PNG, JPG up to 5MB each
                  </span>
                </label>
              </div>
            </div>

              {/* URL Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Or Add Image URL
                </label>
                <div className="flex space-x-2">
                  <input
                    type="url"
                    id="image-url-input"
                    placeholder="https://example.com/image.jpg"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('image-url-input');
                      if (input.value) {
                        handleImageUrlAdd(input.value);
                        input.value = '';
                      }
                    }}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>

              {errors.images && (
                <p className="text-red-600 text-sm mb-4">{errors.images}</p>
              )}

              {/* Hidden input for replacing a single image in place */}
              <input
                ref={replaceInputRef}
                type="file"
                accept="image/*"
                onChange={handleReplaceImageChange}
                className="hidden"
              />

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Images ({imagePreviews.length})
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Drag images to reorder. Position 1 is the main product image.
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    {imagePreviews.map((preview, index) => (
                      <div
                        key={`${preview}-${index}`}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragOver={(event) => handleDragOver(event, index)}
                        onDrop={(event) => handleDrop(event, index)}
                        onDragEnd={handleDragEnd}
                        className={`relative cursor-move rounded-lg ${
                          draggedIndex === index ? 'opacity-50' : ''
                        } ${
                          dragOverIndex === index && draggedIndex !== index
                            ? 'ring-2 ring-indigo-500'
                            : ''
                        }`}
                      >
                        <img 
                          src={preview} 
                          alt={`Product ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            setErrors(prev => ({ ...prev, images: 'Invalid image URL' }));
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          title="Remove image"
                          className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => startReplaceImage(index)}
                          title="Replace this image"
                          className="absolute top-1 left-1 p-1 bg-indigo-600 text-white rounded-full hover:bg-indigo-700"
                        >
                          <RefreshCw size={14} />
                        </button>
                        <div className="absolute bottom-1 left-1 bg-black bg-opacity-60 text-white text-xs px-2 py-1 rounded">
                          {index + 1}{index === 0 ? ' • Main' : ''}
                        </div>
                        <div className="absolute bottom-1 right-1 flex space-x-1">
                          <button
                            type="button"
                            onClick={() => moveImage(index, index - 1)}
                            disabled={index === 0}
                            title="Move earlier"
                            className="p-1 bg-black bg-opacity-60 text-white rounded hover:bg-opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveImage(index, index + 1)}
                            disabled={index === imagePreviews.length - 1}
                            title="Move later"
                            className="p-1 bg-black bg-opacity-60 text-white rounded hover:bg-opacity-80 disabled:opacity-30 disabled:cursor-not-allowed"
                          >
                            <ChevronRight size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Product Preview */}
            {formData.name && formData.price && imagePreviews.length > 0 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Product Preview</h3>
                <div className="border rounded-lg p-4 bg-gray-50">
                  <img 
                    src={imagePreviews[0]} 
                    alt="Product preview"
                    className="w-full h-32 object-cover rounded-lg mb-2"
                  />
                  {imagePreviews.length > 1 && (
                    <p className="text-xs text-gray-500 mb-2">+{imagePreviews.length - 1} more image{imagePreviews.length > 2 ? 's' : ''}</p>
                  )}
                  <h4 className="font-semibold text-gray-900">{formData.name}</h4>
                  <p className="text-xl font-bold text-indigo-600">${formData.price}</p>
                  {selectedCollection && (
                    <p className="text-sm text-gray-600 flex items-center mt-1">
                      <Package size={12} className="mr-1" />
                      {selectedCollection.name}
                    </p>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {formData.sizes.slice(0, 3).map(size => (
                      <span key={size} className="px-2 py-1 text-xs bg-gray-200 rounded">
                        {size}: {formData.sizeStock[size] || 0}
                      </span>
                    ))}
                    {formData.sizes.length > 3 && (
                      <span className="px-2 py-1 text-xs bg-gray-200 rounded">
                        +{formData.sizes.length - 3} more
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Total: {Object.values(formData.sizeStock).reduce((sum, stock) => sum + (parseInt(stock) || 0), 0)} units
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex space-x-4 pt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3 px-6 rounded-lg font-medium transition-colors ${
              isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {isSubmitting
              ? (isEditMode ? 'Saving Changes...' : 'Creating Product...')
              : (isEditMode ? 'Save Changes' : 'Create Product')}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateProduct; 